'use client';

import { useEffect, useRef, useState } from 'react';
import { Card } from 'konsta/react';

declare global {
  interface Window {
    liff?: {
      init: (options: { liffId: string }) => Promise<void>;
      isInClient: () => boolean;
      isLoggedIn: () => boolean;
      login: (options?: { redirectUri?: string }) => void;
      getIDToken: () => string | null;
      permission?: {
        query: (permission: 'openid' | 'profile' | 'email' | 'chat_message.write') => Promise<{
          state: 'granted' | 'prompt' | 'unavailable';
        }>;
        requestAll?: () => Promise<void>;
      };
    };
  }
}

const LIFF_SDK_URL = 'https://static.line-scdn.net/liff/edge/2/sdk.js';

function loadLiffSdk() {
  if (window.liff) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${LIFF_SDK_URL}"]`);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('LINE LIFF SDK 載入失敗')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = LIFF_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('LINE LIFF SDK 載入失敗'));
    document.head.appendChild(script);
  });
}

type LineLoginBootstrapProps = {
  liffId?: string | null;
};

export function LineLoginBootstrap({ liffId }: LineLoginBootstrapProps) {
  const [message, setMessage] = useState('正在連接 LINE 會員...');
  const didRunRef = useRef(false);

  useEffect(() => {
    if (!liffId || didRunRef.current) {
      return;
    }

    const activeLiffId = liffId;
    didRunRef.current = true;

    async function loginWithLine() {
      try {
        await loadLiffSdk();

        if (!window.liff) {
          throw new Error('LINE LIFF SDK 尚未初始化');
        }

        await window.liff.init({ liffId: activeLiffId });

        if (!window.liff.isLoggedIn()) {
          setMessage('正在前往 LINE 登入...');
          window.liff.login({ redirectUri: window.location.href });
          return;
        }

        const openIdPermission = await window.liff.permission?.query('openid').catch(() => null);

        if (openIdPermission?.state === 'unavailable') {
          throw new Error('目前這個 LIFF App 尚未啟用 openid scope，請到 LINE Developers Console 的 LIFF 設定勾選 openid。');
        }

        if (openIdPermission?.state === 'prompt' && window.liff.permission?.requestAll) {
          setMessage('正在請求 LINE 會員權限...');
          await window.liff.permission.requestAll();
        }

        const idToken = window.liff.getIDToken();

        if (!idToken) {
          throw new Error('無法取得 LINE ID Token，請確認 LIFF 已啟用 openid scope，且使用者已同意授權。');
        }

        const response = await fetch('/api/line/session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ idToken }),
        });

        if (!response.ok) {
          const result = await response.json().catch(() => null) as { error?: string } | null;
          throw new Error(result?.error || 'LINE 會員登入失敗');
        }

        setMessage('LINE 會員登入完成，正在重新整理...');
        window.location.assign('/home');
      } catch (error) {
        console.error('LINE MiniApp 登入失敗:', error);
        setMessage(error instanceof Error ? error.message : 'LINE 會員登入失敗，請稍後再試');
      }
    }

    void loginWithLine();
  }, [liffId]);

  if (!liffId) {
    return (
      <Card header="尚未設定 LIFF ID">
        <p>請先在商店後台完成 LINE MiniApp 設定，才能自動引導會員登入。</p>
      </Card>
    );
  }

  return (
    <Card header="LINE 會員登入">
      <p>{message}</p>
    </Card>
  );
}
